/**
 * Admin Post Moderation Service (16415)
 *
 * API client for the admin post (feed item / journal) moderation endpoints.
 * Mirrors adminModerationService.ts's pattern exactly, including its base-URL
 * routing quirk and its defensive response-shape handling.
 *
 * Routing (mirrors adminModerationService.ts's post-verify fix):
 *   The admin SWA builds with VITE_API_URL pointed directly at TaskManager.API
 *   (not the ChellusMobileGateway), so TaskManager has no `/api/journal/*`
 *   routes to rewrite and every moderation call would 404 live. The Journals
 *   service actually serves these routes directly, WITHOUT a `/journal`
 *   prefix — that prefix was only ever a gateway-only rewrite
 *   (`/api/journal/{everything}` → `/api/…` on the Journals.API host).
 *
 *   Mirrors the per-service base-URL pattern in agentAdminService.ts
 *   (VITE_SERVICEMANAGER_API_URL / SERVICEMANAGER_BASE): when
 *   VITE_JOURNALS_API_URL is set (all deployed builds), requests go straight
 *   to the Journals.API host at `/api/v1/admin/posts/...`. When unset (local
 *   dev), falls back to the relative `/api/journal/v1/admin/posts/...` path,
 *   which the Vite dev proxy / gateway rewrites as before.
 *
 * Endpoints (all admin-only, require Admin role in JWT — fixed contract,
 * api-dev builds the server side against this same shape):
 *   GET  /api/v1/admin/posts/reported             — paginated reported list
 *   POST /api/v1/admin/posts/reported/{id}/resolve — resolve with outcome
 *   POST /api/v1/admin/posts/{journalId}/hide      — hide a post
 *   POST /api/v1/admin/posts/{journalId}/unhide    — unhide a post
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import { COLDSTART_RETRY_CONFIG, type RetryConfig } from '../api/resilientFetch';
import type { HeldWinRow, PagedHeldWins } from '../types/heldWin';
import type {
  ReportOutcome,
  ReportStatus,
  ReportedPostRow,
  ModerationAuditEntry,
  PagedModeratedPosts,
  PostModerationState,
} from '../types/post';

const JOURNALS_BASE: string = import.meta.env.VITE_JOURNALS_API_URL || '';

const ADMIN_POSTS = JOURNALS_BASE
  ? `${JOURNALS_BASE}/api/v1/admin/posts`
  : '/api/journal/v1/admin/posts';

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

interface ApiResult<T> {
  data: T;
  success: boolean;
  error?: string;
}

async function callApi<T>(
  endpoint: string,
  options: RequestInit = {},
  retryConfig?: Partial<RetryConfig>
): Promise<ApiResult<T>> {
  try {
    const response = await authenticatedFetch(
      endpoint,
      {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers ?? {}),
        },
      },
      { retryConfig }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Record<string, unknown>;
      return {
        data: null as T,
        success: false,
        error: (body['message'] as string | undefined) ?? `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return { data: null as T, success: true };
    }

    const data = await response.json() as T;
    return { data, success: true };
  } catch (err) {
    return {
      data: null as T,
      success: false,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}

// ---------------------------------------------------------------------------
// Response normalisation
// ---------------------------------------------------------------------------

/** Page size assumed when the caller does not name one (mirrors the API default). */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Both shapes GET .../admin/posts/reported may return.
 *
 * adminModerationService.ts's own reported-comments endpoint was declared as
 * `IEnumerable<ReportedCommentResponse>` server-side and serialised a BARE
 * ARRAY despite accepting pageIndex/pageSize and being documented as
 * "paginated" — measured live against dev-chelless-journals 2026-08-25.
 * Reading `.items` off that array yields undefined, and the page then
 * evaluates `state.rows.length` on undefined and renders nothing at all.
 *
 * api-dev is building the posts endpoint against the same fixed contract in
 * parallel — this normaliser guards against the identical trap here, whether
 * or not it actually reoccurs. The envelope arm is kept so this keeps working
 * unchanged once/if the API returns one, at which point totalCount becomes
 * authoritative again.
 */
export type ReportedPostsPayload =
  | ReportedPostRow[]
  | { items: ReportedPostRow[]; totalCount: number };

/**
 * Collapses either shape to the envelope the page consumes.
 *
 * For a bare array the server sends no grand total, so the best honest figure
 * is how far the caller has already paged plus what this page returned. A
 * full page therefore leaves "Next" disabled: the client genuinely cannot
 * tell whether more rows exist. That limitation is the API's to remove by
 * returning a real totalCount — it is deliberately not guessed at here.
 */
export function normaliseReportedPosts(
  payload: ReportedPostsPayload,
  pageIndex: number,
  pageSize: number
): { items: ReportedPostRow[]; totalCount: number } {
  if (Array.isArray(payload)) {
    return { items: payload, totalCount: pageIndex * pageSize + payload.length };
  }
  return { items: payload.items ?? [], totalCount: payload.totalCount ?? 0 };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Retrieve paginated reported posts.
 * GET /api/admin/posts/reported?status=&pageIndex=&pageSize=
 */
export async function getReportedPosts(opts: {
  status?: ReportStatus;
  pageIndex?: number;
  pageSize?: number;
}): Promise<{ items: ReportedPostRow[]; totalCount: number }> {
  const params = new URLSearchParams();
  if (opts.status !== undefined) params.set('status', opts.status);
  if (opts.pageIndex !== undefined) params.set('pageIndex', String(opts.pageIndex));
  if (opts.pageSize !== undefined) params.set('pageSize', String(opts.pageSize));

  const qs = params.toString();
  const url = `${ADMIN_POSTS}/reported${qs ? `?${qs}` : ''}`;

  const result = await callApi<ReportedPostsPayload>(url, {}, COLDSTART_RETRY_CONFIG);
  if (!result.success || !result.data) {
    throw new Error(result.error ?? 'Failed to fetch reported posts');
  }
  return normaliseReportedPosts(
    result.data,
    opts.pageIndex ?? 0,
    opts.pageSize ?? DEFAULT_PAGE_SIZE
  );
}

/**
 * Resolve a report with an outcome decision.
 * POST /api/admin/posts/reported/{reportId}/resolve
 */
export async function resolvePostReport(reportId: string, outcome: ReportOutcome): Promise<void> {
  const result = await callApi<void>(`${ADMIN_POSTS}/reported/${reportId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ Outcome: outcome }),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to resolve report');
  }
}

/**
 * Hide a post (admin only, reversible). `reason` is written to the
 * `JournalModerationAudit` trail (work item 16414) when the server supports it —
 * harmless to omit against the older unaudited endpoint shape too.
 * POST /api/admin/posts/{journalId}/hide
 */
export async function hidePost(journalId: number, reason?: string): Promise<void> {
  const result = await callApi<void>(`${ADMIN_POSTS}/${journalId}/hide`, {
    method: 'POST',
    body: JSON.stringify(reason !== undefined ? { Reason: reason } : {}),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to hide post');
  }
}

/**
 * Unhide a previously hidden post (admin only).
 * POST /api/admin/posts/{journalId}/unhide
 */
export async function unhidePost(journalId: number, reason?: string): Promise<void> {
  const result = await callApi<void>(`${ADMIN_POSTS}/${journalId}/unhide`, {
    method: 'POST',
    body: JSON.stringify(reason !== undefined ? { Reason: reason } : {}),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to unhide post');
  }
}

// ---------------------------------------------------------------------------
// All-posts moderation queue (16414) — consolidated here from ForSuccessFrontend
// by work item 16532: an admin-only surface belongs in the admin app, not the
// consumer bundle. Mirrors ForSuccessFrontend's postModerationService.ts's
// admin-only half exactly, adapted to this file's callApi/ADMIN_POSTS base.
// ---------------------------------------------------------------------------

/**
 * Retrieve the paginated admin moderation queue of ALL user-authored feed posts
 * (not just reported ones), optionally filtered by moderation state.
 * GET /api/v1/admin/posts?state=&page=&pageSize=
 *
 * `page` is ZERO-BASED — pass `page: 0` for the first page. The `!== undefined` guard
 * (not a truthiness check) is what keeps `page: 0` in the query string.
 */
export async function getModerationQueue(opts: {
  state?: PostModerationState;
  /** `any` (or omitted) sends nothing; `user` = user posts only; otherwise a producer name. */
  producer?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<PagedModeratedPosts> {
  const params = new URLSearchParams();
  if (opts.state !== undefined) params.set('state', opts.state);
  if (opts.producer !== undefined && opts.producer !== 'any') params.set('producer', opts.producer);
  if (opts.page !== undefined) params.set('page', String(opts.page));
  if (opts.pageSize !== undefined) params.set('pageSize', String(opts.pageSize));

  const query = params.toString();
  const url = `${ADMIN_POSTS}${query ? `?${query}` : ''}`;

  const result = await callApi<PagedModeratedPosts>(url, {}, COLDSTART_RETRY_CONFIG);
  if (!result.success || !result.data) {
    throw new Error(result.error ?? 'Failed to fetch moderation queue');
  }
  return result.data;
}

/**
 * Remove a post (admin only). This is a soft delete — the row survives so the audit
 * trail pointing at it stays meaningful — and is removed from every feed read path.
 * POST /api/admin/posts/{journalId}/remove
 */
export async function removePost(journalId: number, reason?: string): Promise<void> {
  const result = await callApi<void>(`${ADMIN_POSTS}/${journalId}/remove`, {
    method: 'POST',
    body: JSON.stringify(reason !== undefined ? { Reason: reason } : {}),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to remove post');
  }
}

/**
 * Retrieve the full moderation history for one post, newest first (admin only).
 * GET /api/admin/posts/{journalId}/audit
 */
export async function getModerationAudit(journalId: number): Promise<ModerationAuditEntry[]> {
  const result = await callApi<ModerationAuditEntry[]>(`${ADMIN_POSTS}/${journalId}/audit`);
  if (!result.success || !result.data) {
    throw new Error(result.error ?? 'Failed to fetch moderation audit');
  }
  return result.data;
}

/**
 * Held Share a Win posts awaiting an admin decision, oldest first (24451).
 * GET /api/v1/admin/posts/held-wins?page=&pageSize=
 */
export async function getHeldWins(page = 0, pageSize = DEFAULT_PAGE_SIZE): Promise<PagedHeldWins> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const result = await callApi<PagedHeldWins | HeldWinRow[]>(
    `${ADMIN_POSTS}/held-wins?${params.toString()}`,
    {},
    COLDSTART_RETRY_CONFIG
  );
  if (!result.success || !result.data) {
    throw new Error(result.error ?? 'Failed to fetch held wins');
  }
  if (Array.isArray(result.data)) {
    return { items: result.data, totalCount: page * pageSize + result.data.length };
  }
  return { items: result.data.items ?? [], totalCount: result.data.totalCount ?? 0 };
}

/**
 * Approve a held win: the server posts it to the feed. 409 when it is no longer held.
 * POST /api/v1/admin/posts/held-wins/{id}/approve
 */
export async function approveHeldWin(
  milestoneRecordId: number,
  reason?: string
): Promise<{ milestoneRecordId: number; journalId: number }> {
  const result = await callApi<{ milestoneRecordId: number; journalId: number }>(
    `${ADMIN_POSTS}/held-wins/${milestoneRecordId}/approve`,
    { method: 'POST', body: JSON.stringify({ reason }) }
  );
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to approve held win');
  }
  return result.data;
}

/**
 * Reject a held win: it is closed and never posted. 409 when it is no longer held.
 * POST /api/v1/admin/posts/held-wins/{id}/reject
 */
export async function rejectHeldWin(milestoneRecordId: number, reason?: string): Promise<void> {
  const result = await callApi<void>(`${ADMIN_POSTS}/held-wins/${milestoneRecordId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to reject held win');
  }
}

export const adminPostModerationService = {
  getReportedPosts,
  resolvePostReport,
  hidePost,
  unhidePost,
  getModerationQueue,
  removePost,
  getModerationAudit,
  getHeldWins,
  approveHeldWin,
  rejectHeldWin,
};
